'use client';
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { bookingsService, type Booking } from '@/lib/supabase/bookingDataService';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SERVICE_COLORS = [
  'bg-blue-500/20 border-blue-500/40 text-blue-300',
  'bg-emerald-500/20 border-emerald-500/40 text-emerald-300',
  'bg-violet-500/20 border-violet-500/40 text-violet-300',
  'bg-primary/20 border-primary/40 text-primary',
  'bg-pink-500/20 border-pink-500/40 text-pink-300',
  'bg-orange-500/20 border-orange-500/40 text-orange-300',
  'bg-cyan-500/20 border-cyan-500/40 text-cyan-300',
];

function getServiceColor(service: string): string {
  let hash = 0;
  for (let i = 0; i < service.length; i++) hash = service.charCodeAt(i) + ((hash << 5) - hash);
  return SERVICE_COLORS[Math.abs(hash) % SERVICE_COLORS.length];
}

function getWeekBounds(offset: number): { monday: Date; sunday: Date } {
  const now = new Date();
  const day = now.getDay();
  const diffToMon = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMon + offset * 7);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { monday, sunday };
}

export default function AdminWeeklyCalendar() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { monday, sunday } = getWeekBounds(weekOffset);

  useEffect(() => {
    setIsLoading(true);
    const monStr = monday.toISOString().split('T')[0];
    const sunStr = sunday.toISOString().split('T')[0];

    const supabaseClient = (async () => {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .gte('appointment_date', monStr)
        .lte('appointment_date', sunStr)
        .order('appointment_time', { ascending: true });

      if (!error && data) {
        const { bookingsService: svc } = await import('@/lib/supabase/bookingDataService');
        // map raw rows
        const mapped: Booking[] = (data as any[]).map((row) => ({
          id: row.id,
          bookingRef: `DB-${row.id.slice(-8).toUpperCase()}`,
          client: `${row.customer_first_name} ${row.customer_last_name}`,
          email: row.customer_email,
          phone: row.customer_phone ?? '',
          service: row.service_name,
          vehicle: `${row.vehicle_year ?? ''} ${row.vehicle_make ?? ''} ${row.vehicle_model ?? ''}`.trim(),
          vehicleMake: row.vehicle_make ?? '',
          vehicleModel: row.vehicle_model ?? '',
          vehicleYear: row.vehicle_year ?? '',
          vehicleColor: row.vehicle_color ?? '',
          date: row.appointment_date ?? '',
          time: row.appointment_time ?? '',
          status: row.booking_status ?? 'pending',
          paymentStatus: row.payment_status,
          paymentType: row.payment_type,
          amount: Number(row.amount_paid),
          deposit: row.payment_type === 'deposit' ? Number(row.amount_paid) : 0,
          addons: row.addons ?? [],
          notes: row.notes ?? '',
          createdAt: row.created_at,
        }));
        setBookings(mapped);
      }
      setIsLoading(false);
    });
    supabaseClient();
  }, [weekOffset]);

  // Build day dates for this week
  const weekDates = DAY_LABELS.map((_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  const todayStr = new Date().toISOString().split('T')[0];

  // Group bookings by date string
  const byDate: Record<string, Booking[]> = {};
  bookings.forEach((b) => {
    if (!b.date) return;
    byDate[b.date] = [...(byDate[b.date] ?? []), b];
  });

  const weekLabel = `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-foreground">Weekly Schedule</p>
          <p className="text-xs text-muted-foreground">{weekLabel}</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWeekOffset((o) => o - 1)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150 active:scale-95"
          >
            <ChevronLeft size={15} />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className="px-3 py-1 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150"
          >
            This Week
          </button>
          <button
            onClick={() => setWeekOffset((o) => o + 1)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150 active:scale-95"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading schedule...</span>
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <div className="min-w-[640px]">
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-border">
              {DAY_LABELS.map((day, i) => {
                const dateStr = weekDates[i].toISOString().split('T')[0];
                const isToday = dateStr === todayStr;
                const isWeekend = day === 'Sat' || day === 'Sun';
                return (
                  <div
                    key={`week-header-${day}`}
                    className={`px-3 py-3 text-center border-r last:border-r-0 border-border ${isWeekend ? 'bg-muted/20' : ''}`}
                  >
                    <p className={`text-xs font-semibold ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>{day}</p>
                    <p className={`text-lg font-bold mt-0.5 ${isToday ? 'text-primary' : isWeekend ? 'text-muted-foreground/40' : 'text-foreground'}`}>
                      {weekDates[i].getDate()}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Events */}
            <div className="grid grid-cols-7">
              {DAY_LABELS.map((day, i) => {
                const dateStr = weekDates[i].toISOString().split('T')[0];
                const events = byDate[dateStr] ?? [];
                const isWeekend = day === 'Sat' || day === 'Sun';
                return (
                  <div
                    key={`week-col-${day}`}
                    className={`min-h-[120px] px-2 py-2 border-r last:border-r-0 border-border space-y-1.5 ${isWeekend ? 'bg-muted/10' : ''}`}
                  >
                    {events.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <span className="text-xs text-muted-foreground/30">Open</span>
                      </div>
                    ) : (
                      events.map((event, ei) => (
                        <div
                          key={`event-${day}-${ei}`}
                          className={`px-2 py-1.5 rounded-md border text-xs ${getServiceColor(event.service)} cursor-pointer hover:opacity-80 transition-opacity`}
                        >
                          <p className="font-semibold truncate">{event.time || '—'}</p>
                          <p className="truncate opacity-80">{event.client}</p>
                          <p className="truncate opacity-60 text-[10px]">{event.service}</p>
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}